import { Controller, Get, Post, Body, Patch, Param, Delete, HttpStatus, Req, Query, DefaultValuePipe, ParseIntPipe } from '@nestjs/common';
import { ApiTags, ApiOperation, ApiResponse, ApiParam, ApiQuery } from '@nestjs/swagger';
import { FastifyRequest } from 'fastify';
import { User } from '../../user/entities/user.entity';
import { NewsfeedService } from '../services/newsfeed.service';
import { CreateNewsfeedDto } from '../dto/create-newsfeed.dto';
import { UpdateNewsfeedDto } from '../dto/update-newsfeed.dto';
import { ApiResponseDto } from '../../../common/dtos/api-response.dto';
import { NewsfeedResponseDto } from '../dto/newsfeed-response.dto';
import { EntityManager } from '@mikro-orm/core';
import { File } from '../../file/entities/file.entity';
import { Tag } from '../entities/tag.entity';
import { Newsfeed } from '../entities/newsfeed.entity';
import { request } from 'http';

@ApiTags('Newsfeed')
@Controller('newsfeed')
export class NewsfeedController {
  constructor(private readonly newsfeedService: NewsfeedService, private readonly em: EntityManager) { }

  @Post()
  @ApiOperation({ summary: 'Create a new newsfeed' })
  @ApiResponse({
    status: 201,
    description: 'The newsfeed has been successfully created.',
    type: NewsfeedResponseDto
  })
  @ApiResponse({ status: 400, 'description': 'Bad Request' })
  async create(
    @Req() request: FastifyRequest & { user: User },
    @Body() createNewsfeedDto: CreateNewsfeedDto
  ): Promise<ApiResponseDto<NewsfeedResponseDto>> {
    console.log('<---------------createNewsfeedDto-------------->', createNewsfeedDto);
    const newsfeed = await this.newsfeedService.create(createNewsfeedDto, request.user);

    const files = await this.em.find(File, { module: 'newsfeed', url: { $in: createNewsfeedDto?.files } });

    files?.forEach(file => {
      file.module_id = newsfeed.id;
    });


    await this.em.flush();
    newsfeed.files = files;


    return {
      success: true,
      message: 'Newsfeed created successfully',
      data: newsfeed,
      statusCode: HttpStatus.CREATED
    };
  }

  @Get()
  @ApiOperation({ summary: 'Get all newsfeeds with pagination' })
  @ApiQuery({
    name: 'page',
    description: 'The page number | default is 1',
    example: '1',
    required: false
  })
  @ApiQuery({
    name: 'limit',
    description: 'The number of items per page | default is 10',
    example: '10',
    required: false
  })
  @ApiQuery({
    name: 'my_stories',
    description: 'Get my stories | default is false',
    example: 'false',
    required: false
  })
  @ApiQuery({
    name: 'search',
    description: 'Search newsfeeds by title | default is null',
    example: 'announcement',
    required: false
  })
  @ApiQuery({
    name: 'is_public',
    description: 'Get public newsfeeds | default is false',
    example: 'false',
    required: false
  })
  @ApiResponse({
    status: 200,
    description: 'Paginated list of newsfeeds',
    type: [NewsfeedResponseDto]
  })
  async findAll(
    @Req() request: FastifyRequest & { user: User },
    @Query('page') page?: number,
    @Query('limit') limit?: number,
    @Query('my_stories') my_stories?: boolean,
    @Query('search') search?: string,
    @Query('is_public') is_public?: boolean,
  ): Promise<ApiResponseDto<NewsfeedResponseDto[]>> {
    page = page || 1;
    limit = limit || 10;
    const newsfeeds = await this.newsfeedService.findAll(page, limit, { my_stories, search, is_public }, request);

    // append files
    for (let i = 0; i < newsfeeds.data.length; i++) {
      const newsfeed = newsfeeds.data[i];
      newsfeed.files = await this.em.find(File, { module: 'newsfeed', module_id: newsfeed.id });
    }

    return {
      success: true,
      message: 'Newsfeeds retrieved successfully',
      data: newsfeeds.data,
      page: newsfeeds.page,
      limit: newsfeeds.limit,
      totalRecords: newsfeeds.total,
      totalPages: newsfeeds.total ? Math.ceil(newsfeeds.total / limit) : 0,
      statusCode: HttpStatus.OK
    };
  }

  @Get(':id')
  @ApiOperation({ summary: 'Get a specific newsfeed by ID' })
  @ApiParam({
    name: 'id',
    description: 'The ID of the newsfeed',
    example: '1'
  })
  @ApiResponse({
    status: 200,
    description: 'The found newsfeed',
    type: NewsfeedResponseDto
  })
  @ApiResponse({ status: 404, description: 'Newsfeed not found' })
  async findOne(@Param('id') id: number): Promise<ApiResponseDto<NewsfeedResponseDto>> {
    const newsfeed = await this.newsfeedService.findOne(id);

    const files = await this.em.find(File, { module: 'newsfeed', module_id: id });
    newsfeed.files = files;
    return {
      success: true,
      message: 'Newsfeed retrieved successfully',
      data: newsfeed,
      statusCode: HttpStatus.OK
    };
  }

  @Patch(':id')
  @ApiOperation({ summary: 'Update a newsfeed' })
  @ApiParam({
    name: 'id',
    description: 'The ID of the newsfeed to update',
    example: '1'
  })
  @ApiResponse({
    status: 200,
    description: 'The newsfeed has been successfully updated',
    type: NewsfeedResponseDto
  })
  @ApiResponse({ status: 404, description: 'Newsfeed not found' })
  @ApiResponse({ status: 400, description: 'Bad Request' })
  async update(
    @Param('id') id: number,
    @Body() updateNewsfeedDto: UpdateNewsfeedDto,
    @Req() request: FastifyRequest & { user: User }
  ): Promise<ApiResponseDto<NewsfeedResponseDto>> {

    // delete tags in tags table and insert new tags in tags table    
    if (updateNewsfeedDto.tags) {
      const tags = await this.em.find(Tag, { newsfeed: { id } });
      tags.forEach(tag => {
        this.em.remove(tag);
      });
      for (let i = 0; i < updateNewsfeedDto.tags.length; i++) {
        const tag = updateNewsfeedDto.tags[i];
        const tagEntity = new Tag();
        tagEntity.title = tag;
        tagEntity.newsfeed = await this.em.findOne(Newsfeed, { id });
        this.em.persist(tagEntity);
      }
    }

    // delete files in file table and insert new files in file table
    if(updateNewsfeedDto.files){
      const files = await this.em.find(File, { module: 'newsfeed', module_id: id });
      files.forEach(file => {
        this.em.remove(file);
      });
      for (let i = 0; i < updateNewsfeedDto.files.length; i++) {
        const file = updateNewsfeedDto.files[i];
        const fileEntity = new File();
        fileEntity.url = file;
        fileEntity.module = 'newsfeed';
        fileEntity.module_id = id;
        fileEntity.user = request.user;
        this.em.persist(fileEntity);
      }
    }
    await this.em.flush();

    delete updateNewsfeedDto.tags;
    const newsfeed = await this.newsfeedService.update(id, updateNewsfeedDto);

    const files = await this.em.find(File, { module: 'newsfeed', module_id: id });
    newsfeed.files = files;
    return {
      success: true,
      message: 'Newsfeed updated successfully',
      data: newsfeed,
      statusCode: HttpStatus.OK
    };
  }

  @Delete(':id')
  @ApiOperation({ summary: 'Delete a newsfeed' })
  @ApiParam({
    name: 'id',
    description: 'The ID of the newsfeed to delete',
    example: '1'
  })
  @ApiResponse({
    status: 200,
    description: 'The newsfeed has been successfully deleted'
  })
  @ApiResponse({ status: 404, description: 'Newsfeed not found' })
  async remove(@Param('id') id: number): Promise<ApiResponseDto<void>> {
    await this.newsfeedService.remove(id);
    return {
      success: true,
      message: 'Newsfeed deleted successfully',
      statusCode: HttpStatus.OK,
      data: undefined
    };
  }
}
